import isEmpty from 'lodash/isEmpty';
import React, { useContext, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';

import { FormBlock, StepProps } from '@/widgets/ServiceFormTemplate';

import { UniversalDocumentFields } from '@/features/ui/ProfileChapters';
import { universalDocumentFieldsPreview } from '@/features/ui/ProfileChapters/ui/UniversalDocumentFieldsPreview';

import { SelectOption } from '@/shared/types';
import { ConditionView, Grid, StepControlPanel } from '@/shared/ui';

import { allAllowedToChooseERZPassportDocumentTypeCodes } from '@/components/Forms/constants';
import { profileDocumentDataInfoFieldsMapper } from '@/components/Forms/DocumentsData';
import { Loader } from '@/shared/ui';

import { useERZDocumentTypes } from '@/modules/erz/helpers';

import AuthContext from '@/contexts/Auth/AuthContext';

import { FormDocument } from '@/api/types';

import { InsuredDocumentSchema } from '../../model/types';

export const InsuredDocumentData: React.FC<StepProps> = ({ next, prev }) => {
  const { documentsData } = useContext(AuthContext);
  const { control, watch, setValue } = useFormContext<InsuredDocumentSchema>();
  const { data: passportDocumentTypeEntities = [], isLoading: isPassportDocumentTypeEntitiesLoading } =
    useERZDocumentTypes({
      allowedDocumentTypeCodes: allAllowedToChooseERZPassportDocumentTypeCodes,
    });
  const insuredDocument = watch('insuredDocument');
  const documents = watch('documents');

  useEffect(() => {
    const isDocumentFilledByUser = !isEmpty(insuredDocument);

    if (!passportDocumentTypeEntities?.length || isDocumentFilledByUser) return;

    const { passport } = documentsData;
    const documentType = passportDocumentTypeEntities?.find(({ code }) => code === passport?.type);

    // @ts-ignore
    setValue('insuredDocument', documentType);
    setValue(`documents.${documentType?.code}.info`, profileDocumentDataInfoFieldsMapper(passport?.fields));
  }, [insuredDocument, documents, passportDocumentTypeEntities]);

  return (
    <ConditionView if={!isPassportDocumentTypeEntitiesLoading} else={<Loader centered />}>
      <FormBlock title="Дані документа">
        <UniversalDocumentFields
          key="documentFields"
          fieldName="insuredDocument"
          documentTypeEntities={passportDocumentTypeEntities as SelectOption[]}
          layout="service"
          documentRequired
          control={control}
          watch={watch}
        />
      </FormBlock>

      <StepControlPanel prev={prev} next={next} disabledNext={isEmpty(insuredDocument)} />
    </ConditionView>
  );
};

export const InsuredDocumentDataPreview: React.FC = () => {
  const { getValues } = useFormContext<InsuredDocumentSchema>();
  const formData = getValues();

  const insuredDocument = formData.insuredDocument;
  const documentCode =
    insuredDocument && typeof insuredDocument === 'object' && insuredDocument !== null
      ? insuredDocument.code
      : typeof insuredDocument === 'string'
        ? insuredDocument
        : undefined;

  const formDocument: FormDocument = {
    type: insuredDocument,
    info: documentCode ? formData.documents?.[documentCode]?.info : undefined,
  };

  return (
    <Grid
      title="Дані документа"
      data={universalDocumentFieldsPreview({
        documentTypeLabel: 'Тип документа, що посвідчує особу',
        document: formDocument,
      })}
    />
  );
};
